// Implements: F-IDN-012, F-UIX-010
/**
 * P2 `/restablecer/:token`: spend the link's token on a new password
 * (`POST /auth/password-reset/confirm`, sent once).
 *
 * What it refuses itself (v3 actions): a missing new password (focus on it) and a confirmation
 * that does not match (focus on it, `aria-invalid`), with no request. Both are judged on what the
 * fields hold at submit, read from the fields themselves: an autofill that fires no `change` event
 * must not leave a stale draft to compare against. The strength meter is orientative; the server
 * decides. What the server answers:
 * - `204`: «Contraseña actualizada» with the way to sign in (no cookie: every session of that
 *   person was closed, so they sign in with the new password);
 * - `401`: the token is unknown, used or expired — one answer on purpose — so «Este enlace ya no
 *   sirve» with the way to ask for another (the v3 «Vacío» state);
 * - `422`: the server's own sentence (the policy, checked before the token, so the link stays
 *   usable) under the new password; a refusal about a value the person has since edited is stale
 *   and ignored (checklist 14);
 * - anything else: the v3 «No se pudo guardar la contraseña» above the form.
 *
 * The focus goes to the `h1` on open and whenever the card changes (form, done, gone).
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router";

import { asApiError } from "../../../shared/api/asApiError";
import { LOGIN_ROUTE } from "../../../shared/api/nextParam";
import { useTranslation } from "../../../shared/i18n";
import { buttonClassName } from "../../../shared/ui/atoms/Button";
import { RecoveryOutcome } from "../components/RecoveryOutcome";
import { RECOVERY_PATH } from "../components/ForgotPasswordLink";
import { ResetPasswordForm } from "../components/ResetPasswordForm";
import { useConfirmPasswordReset } from "../hooks/usePasswordRecovery";

/** How a link ended: the password was saved, or the link is dead. */
export type ResetOutcome = "done" | "gone";

type View = "form" | ResetOutcome;

export interface ResetPasswordContainerProps {
  readonly token: string;
  /** Open on an outcome already reached (the page after it replaced the token's address). */
  readonly initialView?: ResetOutcome | undefined;
  /** Told once the link ended, so the page can drop the token from the address and the history. */
  readonly onSettled?: ((outcome: ResetOutcome) => void) | undefined;
}

export function ResetPasswordContainer({ token, initialView, onSettled }: ResetPasswordContainerProps): ReactElement {
  const { t } = useTranslation();
  const confirm = useConfirmPasswordReset();
  const [view, setView] = useState<View>(initialView ?? "form");
  const [password, setPassword] = useState("");
  const [newError, setNewError] = useState<string | undefined>(undefined);
  const [confirmError, setConfirmError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const newRef = useRef<HTMLInputElement | null>(null);
  const confirmRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [view]);

  if (view === "done") {
    return (
      <RecoveryOutcome
        icon="check-circle"
        title={t("auth.reset.done.title")}
        body={t("auth.reset.done.body")}
        headingRef={headingRef}
        actions={
          <Link to={LOGIN_ROUTE} className={buttonClassName("primary")}>
            {t("auth.reset.done.action")}
          </Link>
        }
      />
    );
  }
  if (view === "gone") {
    return (
      <RecoveryOutcome
        icon="history"
        failed
        title={t("auth.reset.gone.title")}
        body={t("auth.reset.gone.body")}
        headingRef={headingRef}
        actions={
          <Link to={RECOVERY_PATH} className={buttonClassName("primary")}>
            {t("auth.reset.gone.action")}
          </Link>
        }
      />
    );
  }

  const submit = (): void => {
    setFormError(undefined);
    setNewError(undefined);
    setConfirmError(undefined);
    // What the fields hold now, not the last `change` they reported (an autofill may report none).
    const sent = newRef.current?.value ?? password;
    const repeated = confirmRef.current?.value ?? "";
    if (sent !== password) setPassword(sent);
    if (sent === "") {
      setNewError(t("auth.reset.missing"));
      newRef.current?.focus();
      return;
    }
    if (sent !== repeated) {
      setConfirmError(t("auth.reset.mismatch"));
      confirmRef.current?.focus();
      return;
    }
    confirm.mutate(
      { token, newPassword: sent },
      {
        onSuccess: () => {
          setView("done");
          onSettled?.("done");
        },
        onError: (cause) => {
          const error = asApiError(cause);
          if (error.status === 401) {
            setView("gone");
            onSettled?.("gone");
          } else if (error.status === 422) {
            if (newRef.current?.value !== sent) return;
            setNewError(error.message);
            newRef.current.focus();
          } else {
            setFormError(t("auth.reset.failed"));
          }
        },
      },
    );
  };

  return (
    <ResetPasswordForm
      password={password}
      onPasswordChange={(value) => {
        setPassword(value);
        setNewError(undefined);
      }}
      onConfirmChange={() => {
        setConfirmError(undefined);
      }}
      onSubmit={submit}
      submitting={confirm.isPending}
      newError={newError}
      confirmError={confirmError}
      formError={formError}
      headingRef={headingRef}
      newRef={newRef}
      confirmRef={confirmRef}
    />
  );
}
